/**
 * RequirementInlineDetail — the expanded detail card rendered inline
 * (not a side drawer). Shared by List + Notes.
 *
 * Full description + user story (role / want / benefit) + acceptance criteria
 * checklist + evidence list.
 *
 * Edits persist via onUpdate (PATCH), debounced for textual fields.
 */
import { useMemo, useRef } from 'react'
import { ListChecks, Plus } from 'lucide-react'
import type {
  Requirement,
  RequirementUserStory,
  RequirementAcceptanceCriterion,
  RequirementMetadata,
} from '@ispot/shared'
import { useInlineEditor } from '@/hooks/useInlineEditor'

interface Props {
  requirement: Requirement
  onUpdate: (id: string, updates: Partial<Requirement>) => Promise<unknown>
  /** Raw persona name (e.g. "Sarah Chen") — used to detect when stored role is a name, not a title. */
  primaryPersonaName?: string
  /** Persona role/title (e.g. "Chief Product Officer") — preferred display value. */
  primaryPersonaRole?: string
}

export function RequirementInlineDetail({ requirement: r, onUpdate, primaryPersonaName, primaryPersonaRole }: Props) {
  const meta = r.metadata ?? {}
  const usObj: RequirementUserStory = useMemo(() => {
    if (typeof meta.userStory === 'string') return { role: '', want: meta.userStory, benefit: '' }
    return meta.userStory ?? { role: '', want: '', benefit: '' }
  }, [meta.userStory])
  const storedRole = usObj.role.trim()
  // If the stored role is the persona's name (not a real role title), show the role title instead
  const roleDisplay = (!storedRole || storedRole === primaryPersonaName)
    ? (primaryPersonaRole || primaryPersonaName || '')
    : storedRole

  const acList: RequirementAcceptanceCriterion[] = useMemo(() => (meta.acceptanceCriteria ?? []).map(ac => ({
    text: ac.text ?? '',
    done: ac.done ?? ac.met ?? false,
  })), [meta.acceptanceCriteria])

  const doneCount = acList.filter(ac => ac.done).length

  async function patchMeta(patch: Partial<RequirementMetadata>) {
    await onUpdate(r.id, { metadata: { ...meta, ...patch } as RequirementMetadata })
  }

  async function patchUserStory(patch: Partial<RequirementUserStory>) {
    await patchMeta({ userStory: { ...usObj, ...patch } })
  }

  async function toggleAc(i: number) {
    const next = acList.map((ac, idx) => idx === i ? { ...ac, done: !ac.done, met: !ac.done } : { ...ac, met: ac.done })
    await patchMeta({ acceptanceCriteria: next })
  }

  async function addAc() {
    const next: RequirementAcceptanceCriterion[] = [...acList, { text: '', done: false, met: false }]
    await patchMeta({ acceptanceCriteria: next })
  }

  async function editAcText(i: number, text: string) {
    const next = acList.map((ac, idx) => idx === i ? { ...ac, text } : ac)
    await patchMeta({ acceptanceCriteria: next })
  }

  return (
    <div className="flex flex-col gap-6">
      {/* User story — solution-tinted left rail, italic prose, no boxed background */}
      <Section title="User story">
        <div
          className="pl-3"
          style={{
            borderLeft: '2px solid var(--color-solution-200, var(--color-solution-50))',
            color: 'var(--color-fg-secondary)',
            fontStyle: 'italic',
            fontSize: 15,
            lineHeight: 1.6,
          }}
        >
          As{' '}
          <EditableInline
            value={roleDisplay}
            placeholder={primaryPersonaRole || primaryPersonaName || 'a role or persona'}
            onCommit={(v) => patchUserStory({ role: v })}
          />
          , I want{' '}
          <EditableInline
            value={usObj.want}
            placeholder="outcome"
            onCommit={(v) => patchUserStory({ want: v })}
          />
          , so that{' '}
          <EditableInline
            value={usObj.benefit}
            placeholder="benefit"
            onCommit={(v) => patchUserStory({ benefit: v })}
          />
          .
        </div>
      </Section>

      {/* Acceptance criteria — prose-flow checklist */}
      <Section
        title={<span className="inline-flex items-center gap-1.5"><ListChecks size={12} />Acceptance criteria</span>}
        headerRight={
          acList.length > 0 ? (
            <span
              className="tabular-nums"
              style={{ fontSize: 11, color: 'var(--color-fg-muted)' }}
            >
              {doneCount} of {acList.length}
            </span>
          ) : undefined
        }
      >
        <ul className="list-none p-0 m-0 flex flex-col" style={{ gap: 4 }}>
          {acList.map((ac, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 py-1 rounded"
              style={{ fontSize: 15, lineHeight: 1.55 }}
            >
              <Check box={!!ac.done} onToggle={() => toggleAc(i)} />
              <EditableText
                value={ac.text}
                placeholder="New criterion"
                singleLine
                onCommit={(v) => editAcText(i, v)}
                className={ac.done ? 'line-through opacity-60' : ''}
              />
            </li>
          ))}
        </ul>
        <button
          onClick={addAc}
          className="mt-1 inline-flex items-center gap-1.5 py-1 text-sm rounded cursor-pointer bg-transparent border-0 transition-colors"
          style={{ color: 'var(--color-fg-muted)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-fg-secondary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fg-muted)')}
        >
          <Plus size={14} /> Add criterion
        </button>
      </Section>

      {r.evidence.length > 0 && (
        <Section title={`Evidence · ${r.evidence.length}`}>
          <div className="flex flex-col gap-1.5">
            {r.evidence.map((e, i) => (
              <div
                key={i}
                style={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  paddingLeft: 12,
                  borderLeft: '2px solid var(--color-warm-200)',
                }}
              >
                <div style={{ color: 'var(--color-fg)', fontWeight: 500 }}>{e.label}</div>
                {e.uri && <div className="truncate" style={{ fontSize: 12, color: 'var(--color-fg-muted)' }}>{e.uri}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}

/* ============================================================================
 * Small building blocks
 * ========================================================================= */

function Section({ title, children, headerRight, style }: {
  title: React.ReactNode
  children: React.ReactNode
  headerRight?: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div style={style}>
      <div
        className="flex items-center justify-between mb-2"
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-fg-muted)',
        }}
      >
        <span>{title}</span>
        {headerRight}
      </div>
      {children}
    </div>
  )
}

function Check({ box, onToggle }: { box: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle() }}
      className="mt-[1px] flex-shrink-0"
      aria-label="Toggle criterion"
      style={{
        width: 14, height: 14, borderRadius: 3,
        background: box ? 'var(--color-fg)' : 'transparent',
        border: `1px solid ${box ? 'var(--color-fg)' : 'var(--color-border-strong)'}`,
        position: 'relative', cursor: 'pointer',
      }}
    >
      {box && (
        <span
          aria-hidden
          style={{
            position: 'absolute', left: 3, top: 0,
            width: 5, height: 8,
            borderRight: '1.6px solid var(--color-bg-card)',
            borderBottom: '1.6px solid var(--color-bg-card)',
            transform: 'rotate(45deg)',
          }}
        />
      )}
    </button>
  )
}

function EditableText({ value, placeholder, onCommit, singleLine, className }: {
  value: string
  placeholder?: string
  onCommit: (v: string) => void
  singleLine?: boolean
  className?: string
}) {
  const handlers = useInlineEditor({ value, onCommit, singleLine })
  return (
    <div
      ref={handlers.ref as React.RefObject<HTMLDivElement>}
      contentEditable
      suppressContentEditableWarning
      onInput={handlers.onInput}
      onKeyDown={handlers.onKeyDown}
      onPaste={handlers.onPaste}
      onBlur={handlers.onBlur}
      data-placeholder={placeholder}
      spellCheck={false}
      className={`outline-none rounded px-1 py-0.5 -mx-1 whitespace-pre-wrap flex-1 min-w-0 ${className ?? ''}`}
      style={{ color: 'var(--color-fg)', fontSize: 15, lineHeight: 1.55 }}
    />
  )
}

function EditableInline({ value, placeholder, onCommit }: {
  value: string
  placeholder?: string
  onCommit: (v: string) => void
}) {
  const handlers = useInlineEditor({ value, onCommit, singleLine: true })
  // Ensure the element renders with a stable ref target; TS complains without
  // the object-ref spread for inline variants.
  const ref = useRef<HTMLSpanElement>(null)
  return (
    <span
      ref={(el) => { ref.current = el; (handlers.ref as { current: HTMLElement | null }).current = el }}
      contentEditable
      suppressContentEditableWarning
      onInput={handlers.onInput}
      onKeyDown={handlers.onKeyDown}
      onPaste={handlers.onPaste}
      onBlur={handlers.onBlur}
      data-placeholder={placeholder}
      spellCheck={false}
      className="outline-none rounded px-1 -mx-0.5"
      style={{
        fontStyle: 'normal', fontWeight: 500, color: 'var(--color-fg)',
        background: 'var(--color-solution-50)',
        boxShadow: '0 0 0 1px var(--color-solution-100, transparent)',
        display: 'inline-block', minWidth: 60,
      }}
    >{value}</span>
  )
}
